import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PdfBrush, PageGen } from "../../pdf/pdf";
import { ETextAlign } from "../../pdf/pdf";
import { ILayoutImageGen } from "../layoutImage";
import { IItemCount } from "@educraft/base";
import { OptsImage } from "../optsImage";
import { GUtil } from "../../pdf/gutil";
import "../../pdf/draw/primitives";
import "../../pdf/draw/string";
import "../../pdf/draw/image";

/**
 * Test worksheet generators - image + answer line layout
 *
 * Shared drawTest() renders a test card with:
 * - Header with "Jméno: " and underline for student name
 * - 10 rows, each with an image on the left and an answer underline
 *
 * Test: 3 landscape pages with 2/3/4 columns
 * Test__1: 1 portrait page, full A4
 * Test__2: 1 landscape page, 2 columns
 * Test__3: 1 landscape page, 3 columns
 * Test__4: 1 landscape page, 4 columns
 *
 * Migrated from C# EduFrame.Img10.Generators.TestBase + Test/Test__1-4
 */

function drawTest(page: PageGen, rect: PdfRect, opts: OptsImage): void {
  const headerSize = PdfUnit.FromMillimeters(20);
  const margin = PdfUnit.FromMillimeters(10);
  const sp = PdfUnit.FromMillimeters(2);

  const innerRect = new PdfRect(
    rect.x + margin,
    rect.y + margin + headerSize,
    rect.width - 2 * margin,
    rect.height - 2 * margin - headerSize
  );

  page.drawRectangle(GUtil.cutPen, null, rect);

  const name = opts.data.lang['Test_Name'] ?? 'Name: ';
  const nameSize = page.measureString(name, GUtil.baseFont);

  const nameRect = new PdfRect(rect.x + margin, rect.y, nameSize.width, margin + headerSize);
  page.drawString(name, GUtil.baseFont, PdfBrush.Black, null, nameRect, ETextAlign.BottomLeft);

  page.drawLine(GUtil.writePen,
    new PdfPoint(rect.x + margin + nameSize.width, rect.y + margin + headerSize),
    new PdfPoint(rect.x + rect.width - margin, rect.y + margin + headerSize)
  );

  const rowHeight = innerRect.height / opts.list.length;
  const imgWidth = rowHeight * 1.2;

  for (let i = 0; i < opts.list.length; i++) {
    const ry = innerRect.y + i * rowHeight;
    const imgRect = new PdfRect(innerRect.x, ry, imgWidth, rowHeight);

    if (i < opts.list.length) {
      page.drawImageCenter(opts.list[i].image, imgRect);
    }

    page.drawLine(GUtil.writePen,
      new PdfPoint(innerRect.x + imgWidth + sp + sp, ry + rowHeight),
      new PdfPoint(innerRect.x + innerRect.width, ry + rowHeight)
    );
  }
}

export class Test__1 implements ILayoutImageGen, IItemCount {
  countDefault(): number { return 10; }
  countMin(): number { return 6; }
  countMax(): number { return 20; }
  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const page = pdf.addPage();
    drawTest(page, new PdfRect(0, 0, PdfSize.A4.width, PdfSize.A4.height), opts);
  }
}

export class Test__2 implements ILayoutImageGen, IItemCount {
  countDefault(): number { return 10; }
  countMin(): number { return 6; }
  countMax(): number { return 20; }
  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const page = pdf.addPage(true);
    for (let p = 0; p < 2; p++) {
      drawTest(page, new PdfRect(PdfSize.A4H.width / 2 * p, 0, PdfSize.A4H.width / 2, PdfSize.A4H.height), opts);
    }
  }
}

export class Test__3 implements ILayoutImageGen, IItemCount {
  countDefault(): number { return 10; }
  countMin(): number { return 6; }
  countMax(): number { return 20; }
  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const page = pdf.addPage(true);
    for (let p = 0; p < 3; p++) {
      drawTest(page, new PdfRect(PdfSize.A4H.width / 3 * p, 0, PdfSize.A4H.width / 3, PdfSize.A4H.height), opts);
    }
  }
}

export class Test__4 implements ILayoutImageGen, IItemCount {
  countDefault(): number { return 10; }
  countMin(): number { return 6; }
  countMax(): number { return 20; }
  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const page = pdf.addPage(true);
    for (let p = 0; p < 4; p++) {
      drawTest(page, new PdfRect(PdfSize.A4H.width / 4 * p, 0, PdfSize.A4H.width / 4, PdfSize.A4H.height), opts);
    }
  }
}
